/**
 * Minimal HTML overlay for immersive Room Selection Scene V2
 *
 * This overlay is designed to be minimal and game-like:
 * - Header with timer and round info
 * - Compact room info badges positioned near each direction
 * - No heavy UI elements - the PIXI scene is the main focus
 */

import { useMemo } from "react";
import { useRaid } from "@/providers/raid-provider";
import { StrokedText } from "@/components/ui/text/stroked-text";
import { SegmentedProgressBar } from "@/components/ui/progress/segmented-progress-bar";
import { PokemonTypeBadge } from "@/components/pokemon/pokemon-type-badge";
import { PlayerRole } from "types/raid-dynamodb";
import {
  RaidRoomOption,
  RaidRoomConfig,
  RaidBossConfig,
  RoomDifficulty,
  RoomRewardType,
  BossTier,
} from "types/raids/rooms";
import classNames from "classnames";
import { isXsScreen } from "@/utils/screen-size-check";

// ============================================================================
// Type Guards
// ============================================================================

function isRoomConfig(room: RaidRoomOption): room is RaidRoomConfig {
  return room.kind === "room";
}

function isBossConfig(room: RaidRoomOption): room is RaidBossConfig {
  return room.kind === "boss";
}

// ============================================================================
// Constants
// ============================================================================

const DIFFICULTY_LABELS: Record<RoomDifficulty, string> = {
  [RoomDifficulty.EASY]: "Easy",
  [RoomDifficulty.NORMAL]: "Normal",
  [RoomDifficulty.HARD]: "Hard",
};

const BOSS_TIER_LABELS: Record<BossTier, string> = {
  [BossTier.MINI_BOSS]: "Mini",
  [BossTier.BOSS]: "Boss",
  [BossTier.EPIC_BOSS]: "Epic",
};

const REWARD_ICONS: Record<RoomRewardType, string> = {
  [RoomRewardType.RECRUIT_POKEMON]: "🎣",
  [RoomRewardType.ITEM]: "🎁",
  [RoomRewardType.FULL_HEAL]: "💚",
  [RoomRewardType.REVIVE]: "💫",
  [RoomRewardType.BOSS_DISCOVERY]: "🔍",
};

// ============================================================================
// Hook for Room Selection State
// ============================================================================

export function useRoomSelectionV2() {
  const { raidState, voteForRoom } = useRaid();

  const roomSelection = raidState.room_selection;
  const players = raidState.players;
  const yourRole = raidState.your_role;
  const currentRound = raidState.current_round;
  const isBossRound = raidState.is_boss_round;
  const raidName = raidState.raid_name ?? "Raid";

  const hasVoted = useMemo(() => {
    if (!roomSelection || !yourRole) return false;
    return roomSelection.votes[yourRole] !== undefined;
  }, [roomSelection, yourRole]);

  const yourVote = useMemo(() => {
    if (!roomSelection || !yourRole) return null;
    return roomSelection.votes[yourRole] ?? null;
  }, [roomSelection, yourRole]);

  const voteCounts = useMemo(() => {
    if (!roomSelection) return {};
    const counts: Record<number, number> = {};
    Object.values(roomSelection.votes).forEach((roomIndex) => {
      if (roomIndex !== undefined) {
        counts[roomIndex] = (counts[roomIndex] || 0) + 1;
      }
    });
    return counts;
  }, [roomSelection]);

  return {
    roomSelection,
    players,
    yourRole,
    currentRound,
    isBossRound,
    raidName,
    hasVoted,
    yourVote,
    voteCounts,
    voteForRoom,
  };
}

// ============================================================================
// Main Overlay Component
// ============================================================================

export interface RaidRoomSelectionOverlayV2Props {
  isVisible: boolean;
}

export const RaidRoomSelectionOverlayV2 = ({
  isVisible,
}: RaidRoomSelectionOverlayV2Props) => {
  const {
    roomSelection,
    currentRound,
    isBossRound,
    raidName,
    hasVoted,
    yourVote,
    voteCounts,
    voteForRoom,
  } = useRoomSelectionV2();

  if (!roomSelection) return null;

  const timeRemaining = roomSelection.time_remaining;
  const isUrgent = timeRemaining <= 10;
  const isCritical = timeRemaining <= 5;
  const roomOptions = roomSelection.room_options.slice(0, 3);

  return (
    <div
      className={classNames(
        "absolute inset-0 flex flex-col pointer-events-none transition-opacity duration-300",
        {
          "opacity-100": isVisible,
          "opacity-0": !isVisible,
        },
      )}
    >
      {/* Header - Timer and Round Info */}
      <div className="pointer-events-auto p-2">
        <CompactHeader
          raidName={raidName}
          currentRound={currentRound}
          isBossRound={isBossRound}
          timeRemaining={timeRemaining}
          isUrgent={isUrgent}
          isCritical={isCritical}
        />
      </div>

      {/* Direction Room Badges - Positioned at top */}
      <div className="flex-1 relative">
        <div className="absolute top-0 left-0 right-0 flex justify-between px-2 pt-16">
          {roomOptions.map((room, index) => (
            <DirectionRoomBadge
              key={room.id}
              room={room}
              index={index}
              voteCount={voteCounts[index] || 0}
              isSelected={yourVote === index}
              onVote={() => voteForRoom(index)}
              disabled={hasVoted && yourVote !== index}
            />
          ))}
        </div>
      </div>

      {/* Footer - Vote status */}
      <div className="pointer-events-auto">
        <VoteStatusFooter hasVoted={hasVoted} yourVote={yourVote} />
      </div>
    </div>
  );
};

// ============================================================================
// Compact Header
// ============================================================================

interface CompactHeaderProps {
  raidName: string;
  currentRound: number;
  isBossRound: boolean;
  timeRemaining: number;
  isUrgent: boolean;
  isCritical: boolean;
}

const CompactHeader = ({
  raidName,
  currentRound,
  isBossRound,
  timeRemaining,
  isUrgent,
  isCritical,
}: CompactHeaderProps) => {
  const timerVariant = isCritical ? "red" : isUrgent ? "orange" : "blue";

  return (
    <div className="bg-black/50 backdrop-blur-sm rounded-lg px-3 py-2">
      {/* Title row */}
      <div className="flex items-center justify-between mb-1">
        <StrokedText
          fontSize={isXsScreen() ? 16 : 22}
          strokeWidth={6}
          strokeColor="#000"
          fillColor="#fff"
        >
          {raidName}
        </StrokedText>
        <StrokedText
          fontSize={isXsScreen() ? 12 : 16}
          strokeWidth={5}
          strokeColor="#000"
          fillColor="#f4eae7"
        >
          {isBossRound ? "BOSS" : `Room ${currentRound + 1}/10`}
        </StrokedText>
      </div>

      {/* Timer */}
      <div className="relative">
        {timeRemaining <= 0 ? (
          <div className="flex items-center justify-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            <span className="text-white text-sm font-bold">Starting...</span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <SegmentedProgressBar
              ratio={timeRemaining / 30}
              variant={timerVariant as "red" | "orange" | "blue"}
              maxSquares={30}
              segmented={false}
              className={classNames("h-2 flex-1 rounded-full overflow-hidden", {
                "animate-pulse": isCritical,
              })}
            />
            <StrokedText
              fontSize={18}
              strokeWidth={6}
              strokeColor="#000"
              fillColor="#fff"
            >
              {String(Math.ceil(timeRemaining)).padStart(2, "0")}s
            </StrokedText>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Direction Room Badge (Compact)
// ============================================================================

interface DirectionRoomBadgeProps {
  room: RaidRoomOption;
  index: number;
  voteCount: number;
  isSelected: boolean;
  onVote: () => void;
  disabled: boolean;
}

const DirectionRoomBadge = ({
  room,
  index,
  voteCount,
  isSelected,
  onVote,
  disabled,
}: DirectionRoomBadgeProps) => {
  const isBoss = isBossConfig(room);
  const isRoom = isRoomConfig(room);

  const difficultyLabel = isRoom
    ? DIFFICULTY_LABELS[room.difficulty]
    : BOSS_TIER_LABELS[room.tier];

  // Position classes based on direction
  const positionClass =
    index === 0 ? "items-start" : index === 2 ? "items-end" : "items-center";

  return (
    <button
      onClick={onVote}
      disabled={disabled}
      className={classNames(
        "flex flex-col pointer-events-auto transition-all duration-150",
        positionClass,
        {
          "scale-110": isSelected,
          "opacity-50": disabled && !isSelected,
        },
      )}
    >
      {/* Compact badge */}
      <div
        className={classNames(
          "rounded-lg px-2 py-1 border-2 transition-all",
          "bg-black/60 backdrop-blur-sm",
          {
            "border-pk-yellow-3 ring-2 ring-pk-yellow-3/50": isSelected,
            "border-white/30 hover:border-white/50": !isSelected && !disabled,
            "border-white/20": disabled,
          },
        )}
      >
        {/* Type badge */}
        <div className="flex items-center gap-1 mb-0.5">
          <PokemonTypeBadge
            type={room.mainType}
            iconSize={12}
            className="text-[10px] py-0"
            hideText={true}
          />
          <span className="text-white text-[10px] font-bold uppercase">
            {room.mainType}
          </span>
        </div>

        {/* Difficulty + reward */}
        <div className="flex items-center justify-between gap-2">
          <span className="text-white/80 text-[9px]">{difficultyLabel}</span>
          {room.extraReward && (
            <span className="text-[10px]">
              {REWARD_ICONS[room.extraReward.type]}
            </span>
          )}
        </div>

        {/* Vote count */}
        {voteCount > 0 && (
          <div className="flex items-center justify-center mt-0.5">
            <span className="text-pk-green-4 text-[10px] font-bold">
              {voteCount} {voteCount === 1 ? "vote" : "votes"}
            </span>
          </div>
        )}
      </div>

      {/* Selection indicator */}
      {isSelected && (
        <div className="mt-1 bg-pk-yellow-3 text-pk-gray-1 text-[9px] font-bold px-2 py-0.5 rounded-full">
          YOUR VOTE
        </div>
      )}
    </button>
  );
};

// ============================================================================
// Vote Status Footer
// ============================================================================

interface VoteStatusFooterProps {
  hasVoted: boolean;
  yourVote: number | null;
}

const VoteStatusFooter = ({ hasVoted, yourVote }: VoteStatusFooterProps) => {
  if (!hasVoted) {
    return (
      <div className="bg-black/50 backdrop-blur-sm p-2 text-center">
        <div className="flex items-center justify-center gap-2">
          <span className="text-pk-yellow-3 animate-pulse">▲</span>
          <span className="text-white text-sm font-bold">
            Choose a path to explore
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-pk-green-2/50 backdrop-blur-sm p-2 text-center">
      <span className="text-pk-green-4 text-sm font-bold">
        ✓ Path chosen - waiting for team...
      </span>
    </div>
  );
};

export default RaidRoomSelectionOverlayV2;
